import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, AlertCircle, Clock } from 'lucide-react';
import Footer from './Footer';
import { PortalLogoLink } from './PortalLogoLink';
import { getApiUrl } from '../utils/api';

interface LoginProps {
    onLogin: (isAuthenticated: boolean) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const [blockedUntil, setBlockedUntil] = useState<Date | null>(null);
    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
    const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>(undefined);

    // Countdown timer for blocked IP
    useEffect(() => {
        if (!isBlocked || !blockedUntil) return;

        const updateCountdown = () => {
            const now = new Date();
            const diff = Math.max(0, Math.ceil((blockedUntil.getTime() - now.getTime()) / 1000));
            setRemainingSeconds(diff);

            if (diff === 0) {
                setIsBlocked(false);
                setBlockedUntil(null);
                setRemainingSeconds(null);
                setError('');
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [isBlocked, blockedUntil]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing (but not if blocked)
        if (error && !isBlocked) setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // Call backend login API using centralized API configuration
            const response = await fetch(`${getApiUrl()}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: formData.username,
                    password: formData.password
                })
            });

            // Check if response is OK before parsing JSON
            if (!response.ok) {
                // Try to parse error message, but handle non-JSON responses
                try {
                    const errorData = await response.json();

                    // Handle rate limit (429)
                    if (response.status === 429) {
                        setIsBlocked(true);
                        const retryAfter = errorData.retryAfter || 300; // Default to 5 minutes
                        const blockUntil = new Date(Date.now() + retryAfter * 1000);
                        setBlockedUntil(blockUntil);
                        setRemainingSeconds(retryAfter);
                        setError(errorData.message || 'Too many failed login attempts. Your IP has been temporarily blocked for 5 minutes.');
                        return;
                    }

                    // Handle invalid credentials (401)
                    if (response.status === 401) {
                        setRemainingAttempts(errorData.remainingAttempts);
                        const baseMessage = errorData.message || 'Invalid username or password.';
                        const attemptsMessage = errorData.remainingAttempts !== undefined
                            ? `${baseMessage} ${errorData.remainingAttempts} attempt${errorData.remainingAttempts !== 1 ? 's' : ''} remaining.`
                            : baseMessage;
                        setError(attemptsMessage);
                        return;
                    }

                    setError(errorData.message || `Server error: ${response.status}`);
                } catch {
                    setError(`Failed to connect to server (${response.status}). Please check if the backend is running on port 3001.`);
                }
                return;
            }

            const data = await response.json();

            if (data.success) {
                // Reset rate limit state on successful login
                setIsBlocked(false);
                setBlockedUntil(null);
                setRemainingSeconds(null);
                setRemainingAttempts(undefined);

                // Store JWT token
                localStorage.setItem('token', data.data.token);
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('user', JSON.stringify(data.data.user));

                // Update parent component state
                onLogin(true);

                // Navigate to dashboard
                navigate('/ramp-me');
            } else {
                setError(data.message || 'Invalid username or password. Please try again.');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Failed to connect to server. Please check if the backend is running.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="max-w-md w-full">
                    {/* Logo and Header */}
                    <div className="text-center mb-8">
                        <div className="flex justify-center mb-6">
                            <PortalLogoLink imgClassName="h-20 w-auto" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-800 mb-2">
                            RAMP Dashboard
                        </h1>
                        <p className="text-slate-600">
                            Andhra Pradesh MSME Feedback Analytics
                        </p>
                    </div>

                    {/* Login Form */}
                    <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
                        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                            {/* Username Field */}
                            <div>
                                <label htmlFor="username" className="block text-sm font-medium text-slate-700 mb-2">
                                    Username
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-slate-400" />
                                    </div>
                                    <input
                                        id="username"
                                        name="username"
                                        type="text"
                                        required
                                        autoComplete="off"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        disabled={isBlocked}
                                        className="block w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-100"
                                        placeholder="Enter your username"
                                    />
                                </div>
                            </div>

                            {/* Password Field */}
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                                    Password
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-slate-400" />
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        required
                                        autoComplete="off"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        disabled={isBlocked}
                                        className="block w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-100"
                                        placeholder="Enter your password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className={`flex items-start gap-2 p-3 rounded-lg ${isBlocked
                                    ? 'bg-orange-50 border border-orange-200'
                                    : 'bg-red-50 border border-red-200'
                                    }`}>
                                    {isBlocked ? (
                                        <Clock className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                                    ) : (
                                        <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                                    )}
                                    <div className="flex-1">
                                        <span className={`text-sm ${isBlocked ? 'text-orange-700' : 'text-red-700'
                                            }`}>
                                            {error}
                                        </span>
                                        {isBlocked && remainingSeconds !== null && (
                                            <div className="mt-2 text-xs text-orange-600 font-medium">
                                                Time remaining: {Math.floor(remainingSeconds / 60)}:{(remainingSeconds % 60).toString().padStart(2, '0')}
                                            </div>
                                        )}
                                        {!isBlocked && remainingAttempts !== undefined && remainingAttempts > 0 && (
                                            <div className="mt-1 text-xs text-red-600">
                                                {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining before temporary block
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Login Button */}
                            <button
                                type="submit"
                                disabled={isLoading || isBlocked}
                                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Signing in...
                                    </div>
                                ) : isBlocked ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <Clock className="h-4 w-4" />
                                        Temporarily Blocked
                                    </div>
                                ) : (
                                    'Sign In'
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Login;
